// src/antdviews/EdfTaskView.tsx

// EDF 任务页面

import axios from 'axios'
import React from 'react'
import { Layout, message } from 'antd'
import { withTranslation, WithTranslation } from 'react-i18next'

import Header from './components/Header'
import Sidebar from './components/Sidebar'
import { Global } from '../services/Global'
import { browserUtil } from '../utils/BrowserUtil'
import EdfTaskContent, { EdfTaskItem, EdfTaskData } from './components/EdfTaskContent'
import { encrypUtil } from '../utils/EncrypUtil'
import { DrpApi } from '../services/Api'
import { ROLE } from '../services/Macro'

interface EdfTaskViewState {
    showWarn: boolean
    showConfirm: boolean
    warnTitle: string
    warnInfo: string
    confirmOp: () => void
    tasks: EdfTaskData
    loginName: string
    taskSize: number
    taskPage: number
}

class EdfTaskView extends React.Component<WithTranslation, EdfTaskViewState> {
    state: EdfTaskViewState = {
        showWarn: false,
        showConfirm: false,
        warnTitle: '本地错误',
        warnInfo: '参数错误',
        confirmOp: () => {
            console.log('do nothing!')
        },
        tasks: { list: [], totalPages: 0 },
        loginName: '',
        taskSize: 10,
        taskPage: 1,
    }

    componentWillMount() {
        Global.isMobile = browserUtil.checkMobile()
    }

    componentDidMount() {
        // 拉取 EDF 任务列表
        this.requestTasks()
    }

    /**
     * 请求 EDF 任务列表
     * 后端返回字段（驼峰）: id / taskId / recordUrl / status / currentStep /
     *                      stepMessage / edfSegmentCount / edfSegmentMinutes /
     *                      reportReady / documentId / remark /
     *                      createdAt / updatedAt / finishedAt
     */
    requestTasks = async () => {
        const DEBUG = true
        const TAG = `EdfTaskView.requestTasks() - `
        const uuid = 'system'
        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'

        const requestData = {
            role: ROLE.SUPER,
            token,
            uuid,
            lang: lang.split('_')[0],
            page: this.state.taskPage,
            size: this.state.taskSize,
        }

        try {
            DEBUG && console.log(TAG, `requestData:\n`, requestData)

            const params = await encrypUtil.encodeRequestRsa(requestData)
            DEBUG && console.log(TAG, `params:\n`, params)

            const response = await axios.post(DrpApi.GET_TASK_LIST, params)
            DEBUG && console.log(TAG, `获取EDF任务列表:\n`, response.data.data)

            const tasks = response.data.data as EdfTaskData
            this.handleTasks(tasks)

            this.setState({ tasks })
        } catch (error) {
            console.error(TAG, error)
            message.error('EDF任务获取失败')
        }
    }

    /**
     * 对后端返回的任务数据做前端加工
     * - 生成分页数组
     * - 生成状态展示文本 / 颜色标记
     * - 由 createdAt / updatedAt / finishedAt 计算格式化时间文本
     * - 判断报告是否可查看
     */
    handleTasks = (tasks: EdfTaskData) => {
        const taskPages: number[] = []
        for (let i = 0; i < (tasks.totalPages || 0); i++) {
            taskPages.push(i + 1)
        }
        tasks.pages = taskPages

        tasks.list.forEach((task: EdfTaskItem) => {
            // 状态展示
            task.statusText = this.getStatusText(task.status)
            task.statusColor = this.getStatusColor(task.status)

            // 报告就绪
            task.reportReady = !!task.reportReady
            task.canViewReport = task.reportReady && !!task.documentId

            // 分段信息展示
            task.segmentInfo = `${task.edfSegmentCount || 1} 段 × ${task.edfSegmentMinutes || 60} 分钟`

            // 时间格式化：由 createdAt / updatedAt / finishedAt 计算得到
            task.createdAtText = this.formatTime(task.createdAt)
            task.updatedAtText = this.formatTime(task.updatedAt)
            task.finishedAtText = task.finishedAt ? this.formatTime(task.finishedAt) : '-'

            // 记录URL 截断展示
            task.recordUrlShort =
                task.recordUrl && task.recordUrl.length > 32
                    ? `${task.recordUrl.slice(0, 32)}...`
                    : task.recordUrl || '-'
        })
    }

    getStatusText = (status: string): string => {
        const map: Record<string, string> = {
            CREATED: '已创建',
            UPLOADING: '上传中',
            IMPORTING: '导入中',
            ANALYZING: '分析中',
            REPORT_GENERATING: '报告生成中',
            REPORT_READY: '报告就绪',
            REPORT_PUBLISHED: '报告已发布',
            COMPLETED: '已完成',
            FAILED: '失败',
            CANCELLED: '已取消',
        }
        return map[status] || status || '-'
    }

    getStatusColor = (status: string): string => {
        const map: Record<string, string> = {
            CREATED: 'default',
            UPLOADING: 'blue',
            IMPORTING: 'blue',
            ANALYZING: 'processing',
            REPORT_GENERATING: 'processing',
            REPORT_READY: 'cyan',
            REPORT_PUBLISHED: 'green',
            COMPLETED: 'success',
            FAILED: 'error',
            CANCELLED: 'warning',
        }
        return map[status] || 'default'
    }

    formatTime = (value: string | number | undefined | null): string => {
        if (!value) return '-'
        const d = new Date(value)
        if (isNaN(d.getTime())) return String(value)
        const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)
        return (
            `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
            `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
        )
    }

    changeTaskList = (size: number, page: number) => {
        this.setState(
            {
                taskSize: size,
                taskPage: page,
            },
            this.requestTasks
        )
    }

    changeLang = () => {
    }

    render() {
        const { t, i18n } = this.props

        return (
            <section className="body">
                <Header
                    trans={t}
                    i18n={i18n}
                    loginName={this.state.loginName}
                    changeLang={this.changeLang}
                />

                <Layout>
                    <Sidebar selectMenu="edfTask" trans={t} />

                    <EdfTaskContent
                        tasks={this.state.tasks}
                        trans={t}
                        changeTaskList={this.changeTaskList}
                    />
                </Layout>
            </section>
        )
    }
}

export const EdfTaskPage = withTranslation()(EdfTaskView)